import React from 'react'
import { Box, Chip, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import moment from 'moment'

// Observacion que un middleware externo anexa al log de la transaccion
// (corelogs.middlewareObservation). Es un observador: mira el recorrido HTTP de la
// operacion y describe lo que vio. No participa en la venta ni decide su resultado.
//
// Tres reglas mandan sobre todo lo que se pinta aqui:
//   1. Es opcional. Que no exista no convierte la transaccion en error.
//   2. La causa es una interpretacion de las senales disponibles, no un veredicto:
//      una venta denegada puede traer outcome=success, porque eso solo habla de la
//      comunicacion.
//   3. Lo que no se midio se dice, no se rellena: sin senales de Docker no es "Docker
//      sano", y un cero no es "normal".

const styles = {
  seccion: {
    border: 1,
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'action.hover',
    padding: 1.5,
    marginTop: 1
  },
  titulo: { fontSize: 12, fontWeight: 'bold' },
  subtitulo: { fontSize: 11, fontWeight: 'bold', marginTop: 1, marginBottom: 0.5 },
  nota: { fontSize: 11, color: 'text.secondary' },
  aviso: { fontSize: 11, color: 'warning.main' },
  fila: { display: 'flex', flexWrap: 'wrap' },
  celda: { fontSize: 11, padding: '4px 8px' },
  chip: { fontSize: 10, height: 18 }
}

// El texto tecnico que manda el agente, leido en espanol. Lo que no este en la tabla
// se pinta con su valor original: perder el dato seria peor que verlo crudo.
const OUTCOMES = {
  success: { texto: 'Sin anomalías en la comunicación', color: 'default' },
  upstream_timeout: { texto: 'Tiempo de espera agotado', color: 'warning' },
  upstream_failure: { texto: 'Falló el servicio observado', color: 'error' },
  upstream_error: { texto: 'El servicio respondió con error', color: 'error' },
  client_disconnected: { texto: 'El cliente se desconectó', color: 'warning' },
  observer_expired: { texto: 'El observador expiró sin cierre', color: 'default' }
}

const CAUSAS = {
  container_oom_killed: 'El contenedor murió por memoria (OOM)',
  container_die: 'El contenedor terminó durante la petición',
  container_kill: 'El contenedor recibió kill durante la petición',
  container_stop: 'El contenedor se detuvo durante la petición',
  container_restart: 'El contenedor se reinició durante la petición',
  container_unhealthy: 'El contenedor se reportó unhealthy',
  upstream_timeout: 'Tiempo de espera agotado esperando al servicio',
  client_cancelled: 'El cliente canceló la petición',
  http_transport_error: 'Error de conexión o transporte',
  http_5xx: 'El servicio respondió 5xx'
}

const CONFIANZAS = {
  high: 'Alta',
  medium: 'Media'
}

// Etapa en la que se agoto la espera. Valores documentados; otro se pinta tal cual.
const ETAPAS = {
  response_headers: 'esperando los encabezados de respuesta',
  upstream: 'hablando con el servicio'
}

const SIN_MEDIR = 'No medido'

const vacio = (val) => val === null || val === undefined || val === ''

const texto = (val) => (vacio(val) ? '-' : String(val))

const booleano = (val) => {
  if (val === true) return 'Sí'
  if (val === false) return 'No'
  return '-'
}

const fecha = (val) => (vacio(val) ? '-' : moment(val).format('DD/MM/YYYY HH:mm:ss.SSS'))

const duracion = (ms) => {
  if (vacio(ms) || isNaN(ms)) return '-'
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${ms} ms`
}

const numero = (val) => (vacio(val) || isNaN(val) ? SIN_MEDIR : Number(val).toLocaleString('es-MX'))

const porcentaje = (val) => (vacio(val) || isNaN(val) ? SIN_MEDIR : `${Number(val).toFixed(1)} %`)

// Un cero en un limite o en una memoria puede ser "sin limite" o "no se midio": el
// agente no lo distingue, asi que la pantalla tampoco lo afirma.
const memoria = (val) => {
  if (vacio(val) || isNaN(val)) return SIN_MEDIR
  if (Number(val) === 0) return '0 B (sin límite o no medido)'
  return `${(Number(val) / 1024 / 1024).toFixed(1)} MB`
}

// El error de transporte es texto libre del agente: se recorta antes de pintarlo.
const recorta = (val, largo = 300) => {
  const cadena = String(val)
  return cadena.length > largo ? `${cadena.slice(0, largo)}…` : cadena
}

const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 160, marginBottom: 1, marginRight: 3 }}>
    <Typography sx={{ fontSize: 10, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, wordBreak: 'break-all' }}>{children}</Typography>
  </Box>
)

const MiddlewareObservationView = ({ observation }) => {
  const obs = (observation && typeof observation === 'object' && !Array.isArray(observation)) ? observation : null

  if (!obs)
    return (
      <Box>
        <Divider sx={{ marginY: 1 }} />
        <Typography sx={styles.titulo}>Observación del middleware</Typography>
        <Typography sx={styles.nota}>
          Sin observación del middleware. Es un dato opcional: su ausencia no dice nada del resultado de la transacción.
        </Typography>
      </Box>
    )

  // Solo el marcador que pone QA, y solo cuando vale exactamente true: cualquier otra
  // cosa se trata como observacion real.
  const fixture = (obs.validationFixture && typeof obs.validationFixture === 'object') ? obs.validationFixture : null
  const simulado = fixture?.simulated === true

  const outcome = OUTCOMES[obs.outcome]
  const senales = Array.isArray(obs.dockerSignals) ? obs.dockerSignals : []
  const diagnostico = (obs.diagnostics && typeof obs.diagnostics === 'object') ? obs.diagnostics : null
  const contenedor = (diagnostico?.container && typeof diagnostico.container === 'object') ? diagnostico.container : null
  const recursos = (diagnostico?.resources && typeof diagnostico.resources === 'object') ? diagnostico.resources : null
  const redis = (diagnostico?.redis && typeof diagnostico.redis === 'object') ? diagnostico.redis : null

  const peticion = [obs.method, obs.path].filter((parte) => !vacio(parte)).join(' ')

  return (
    <Box>
      <Divider sx={{ marginY: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={styles.titulo}>Observación del middleware</Typography>
        {simulado && <Chip size="small" color="warning" label="SIMULADO · DEV" sx={styles.chip} />}
      </Box>
      <Typography sx={styles.nota}>
        Interpretación de las señales que dejó el recorrido de la petición. Describe la comunicación,
        no la autorización comercial: el resultado de la venta se lee en la transacción.
      </Typography>

      {simulado && (
        <Typography sx={{ ...styles.aviso, marginTop: 0.5 }}>
          Datos insertados para validar esta pantalla: la causa que se muestra no ocurrió necesariamente y no
          debe usarse como evidencia de un incidente. El resultado de la transacción sí es real.
          {!vacio(fixture?.scenario) && ` Escenario: ${fixture.scenario}.`}
          {!vacio(fixture?.environment) && ` Entorno: ${fixture.environment}.`}
        </Typography>
      )}

      <Box sx={styles.seccion}>
        <Box sx={styles.fila}>
          <Field label="Resultado técnico">
            {vacio(obs.outcome)
              ? 'Sin clasificar'
              : <Chip
                size="small"
                color={outcome ? outcome.color : 'default'}
                label={outcome ? outcome.texto : obs.outcome}
                sx={styles.chip}
              />}
          </Field>
          <Field label="Causa probable">
            {vacio(obs.probableCause) ? 'Sin causa atribuida' : (CAUSAS[obs.probableCause] || obs.probableCause)}
          </Field>
          <Field label="Confianza">
            {vacio(obs.confidence) ? 'Sin clasificar' : (CONFIANZAS[obs.confidence] || obs.confidence)}
          </Field>
        </Box>
        <Typography sx={styles.nota}>
          La confianza dice qué tan concreta fue la regla que clasificó, no qué tan probable es la causa.
          Cuando hay señal de Docker manda sobre el tiempo de espera, así que una petición puede aparecer
          agotada y aun así atribuirse al contenedor.
        </Typography>

        <Typography sx={styles.subtitulo}>Petición observada</Typography>
        <Box sx={styles.fila}>
          <Field label="Método y ruta">{peticion || '-'}</Field>
          <Field label="Inicio">{fecha(obs.startedAt)}</Field>
          <Field label="Fin">{vacio(obs.finishedAt) ? 'Sin cierre registrado' : fecha(obs.finishedAt)}</Field>
          <Field label="Duración observada">{duracion(obs.durationMs)}</Field>
          <Field label="Estado HTTP">{texto(obs.httpStatus)}</Field>
          <Field label="Bytes de respuesta">{numero(obs.responseBytes)}</Field>
          <Field label="Tiempo de espera agotado">
            {booleano(obs.timedOut)}
            {obs.timedOut === true && !vacio(obs.timeoutStage) && ` (${ETAPAS[obs.timeoutStage] || obs.timeoutStage})`}
          </Field>
        </Box>
        {!vacio(obs.upstreamError) && (
          <Field label="Error de transporte">{recorta(obs.upstreamError)}</Field>
        )}
        <Typography sx={styles.nota}>
          Es el tiempo que midió el observador sobre la petición, no el que percibió quien vendió.
          Un 200 no significa venta aprobada, y un 502 por sí solo no prueba que el servicio se haya caído.
        </Typography>

        <Typography sx={styles.subtitulo}>Señales de Docker</Typography>
        {senales.length === 0
          ? <Typography sx={styles.nota}>
            Sin señal Docker correlacionada. No significa que Docker estuviera sano: significa que no se
            asoció ningún evento a la ventana de esta petición.
          </Typography>
          : <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.celda}>Evento</TableCell>
                  <TableCell sx={styles.celda}>Contenedor</TableCell>
                  <TableCell sx={styles.celda}>Momento</TableCell>
                  <TableCell sx={styles.celda}>Atributos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {senales.map((senal, indice) => {
                  const atributos = (senal?.attributes && typeof senal.attributes === 'object') ? senal.attributes : {}
                  const detalle = Object.keys(atributos)
                    .filter((clave) => typeof atributos[clave] !== 'object' && !vacio(atributos[clave]))
                    .map((clave) => `${clave}: ${atributos[clave]}`)
                    .join(' · ')
                  return (
                    <TableRow key={indice}>
                      <TableCell sx={styles.celda}>{texto(senal?.action)}</TableCell>
                      <TableCell sx={styles.celda}>
                        {texto(senal?.container_name)}
                        {!vacio(senal?.container_id) && (
                          <Typography sx={{ fontSize: 10, color: 'text.secondary', wordBreak: 'break-all' }}>
                            {senal.container_id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={styles.celda}>{fecha(senal?.occurred_at)}</TableCell>
                      <TableCell sx={styles.celda}>{detalle || '-'}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <Typography sx={styles.nota}>
              Es coincidencia temporal con el contenedor objetivo, no causa demostrada: conviene comprobar
              que se trata de la réplica que atendió la petición.
            </Typography>
          </>}

        <Typography sx={styles.subtitulo}>Diagnóstico</Typography>
        {!diagnostico
          ? <Typography sx={styles.nota}>
            No medido. La fotografía de contenedor, recursos y Redis se toma sólo ante anomalías.
          </Typography>
          : <>
            <Box sx={styles.fila}>
              <Field label="Tomado a las">{fecha(diagnostico.captured_at)}</Field>
            </Box>

            <Typography sx={{ ...styles.nota, fontWeight: 'bold' }}>Contenedor</Typography>
            {contenedor?.available === false && (
              <Typography sx={styles.aviso}>
                Docker no respondió al inspeccionar: el resto de este bloque puede no ser fiable.
              </Typography>
            )}
            <Box sx={styles.fila}>
              <Field label="Inspección disponible">{booleano(contenedor?.available)}</Field>
              <Field label="En ejecución">{booleano(contenedor?.running)}</Field>
              <Field label="Estado">{vacio(contenedor?.status) ? SIN_MEDIR : contenedor.status}</Field>
              <Field label="Muerto">{booleano(contenedor?.dead)}</Field>
              <Field label="Muerto por memoria">{booleano(contenedor?.oom_killed)}</Field>
              <Field label="Código de salida">{vacio(contenedor?.exit_code) ? SIN_MEDIR : contenedor.exit_code}</Field>
              <Field label="PID">{vacio(contenedor?.pid) ? SIN_MEDIR : contenedor.pid}</Field>
              <Field label="Reinicios">{vacio(contenedor?.restart_count) ? SIN_MEDIR : contenedor.restart_count}</Field>
              <Field label="Salud">{vacio(contenedor?.health) ? SIN_MEDIR : contenedor.health}</Field>
            </Box>
            {!vacio(contenedor?.error) && (
              <Field label="Error al inspeccionar">{recorta(contenedor.error)}</Field>
            )}

            <Typography sx={{ ...styles.nota, fontWeight: 'bold' }}>Recursos</Typography>
            {recursos?.available === false && (
              <Typography sx={styles.aviso}>
                No se pudieron leer las estadísticas: los valores de abajo pueden no ser fiables.
              </Typography>
            )}
            <Box sx={styles.fila}>
              <Field label="Lectura disponible">{booleano(recursos?.available)}</Field>
              <Field label="CPU">{porcentaje(recursos?.cpu_percent)}</Field>
              <Field label="Memoria">{porcentaje(recursos?.memory_percent)}</Field>
              <Field label="Memoria usada">{memoria(recursos?.memory_usage_bytes)}</Field>
              <Field label="Límite de memoria">{memoria(recursos?.memory_limit_bytes)}</Field>
              <Field label="Procesos">{vacio(recursos?.pids) ? SIN_MEDIR : recursos.pids}</Field>
            </Box>
            {!vacio(recursos?.error) && (
              <Field label="Error de la lectura">{recorta(recursos.error)}</Field>
            )}
            <Typography sx={styles.nota}>
              Son porcentajes instantáneos del momento de la fotografía, no el promedio de la transacción.
            </Typography>

            <Typography sx={{ ...styles.nota, fontWeight: 'bold' }}>Redis</Typography>
            <Box sx={styles.fila}>
              <Field label="Configurado">{booleano(redis?.configured)}</Field>
              <Field label="Respondió al PING">{booleano(redis?.reachable)}</Field>
              <Field label="Latencia de la sonda">{vacio(redis?.latency_ms) ? SIN_MEDIR : duracion(redis.latency_ms)}</Field>
              <Field label="Memoria usada">{memoria(redis?.used_memory_bytes)}</Field>
              <Field label="Memoria RSS">{memoria(redis?.used_memory_rss_bytes)}</Field>
              <Field label="Memoria máxima">{memoria(redis?.max_memory_bytes)}</Field>
              <Field label="Fragmentación">
                {vacio(redis?.fragmentation_ratio) || isNaN(redis.fragmentation_ratio)
                  ? SIN_MEDIR
                  : Number(redis.fragmentation_ratio).toFixed(2)}
              </Field>
            </Box>
            {!vacio(redis?.error) && (
              <Field label="Error de la sonda">{recorta(redis.error)}</Field>
            )}
            <Typography sx={styles.nota}>
              La sonda dice cómo estaba Redis en ese instante. Que no respondiera no demuestra que Redis
              haya causado la denegación.
            </Typography>
          </>}

        <Typography sx={styles.subtitulo}>Observador</Typography>
        <Box sx={styles.fila}>
          <Field label="ID de observación">{texto(obs.observationId)}</Field>
          <Field label="ID de petición">{texto(obs.requestId)}</Field>
          <Field label="Contenedor objetivo">{texto(obs.targetContainer)}</Field>
          <Field label="Enriquecido a las">{fecha(obs.recordedAt)}</Field>
        </Box>
        <Typography sx={styles.nota}>
          Identificadores del observador, no de la transacción. El contenedor objetivo es el que se usó para
          asociar señales: con varias réplicas no afirma cuál atendió la petición.
        </Typography>
      </Box>
    </Box>
  )
}

export default MiddlewareObservationView
